// LanguageContext.tsx — Contexto global de idioma
// Prioridad: hostname (dominio) > localStorage > navigator.language
// En producción el botón de idioma redirige al dominio hermano.
import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { translations } from '../i18n/translations';
import type { Language, Translations } from '../i18n/translations';

const DOMAIN_ES = 'construyo.ia.reinvent-x.com';
const DOMAIN_EN = 'build.ai.reinvent-x.com';

interface LanguageContextValue {
  lang: Language;
  t: Translations;
  toggleLang: () => void;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const getInitialLang = (): Language => {
  const hostname = window.location.hostname;
  // 1. Dominio de producción — prioridad máxima
  if (hostname.includes(DOMAIN_EN)) return 'en';
  if (hostname.includes(DOMAIN_ES)) return 'es';
  // 2. Preferencia guardada (localhost / staging)
  try {
    const saved = localStorage.getItem('rx_lang') as Language | null;
    if (saved === 'es' || saved === 'en') return saved;
    // 3. Idioma del navegador como fallback
    return navigator.language.slice(0, 2) === 'en' ? 'en' : 'es';
  } catch { return 'es'; }
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Language>(getInitialLang);

  const toggleLang = useCallback((): void => {
    const hostname = window.location.hostname;

    // En producción: redirigir al dominio del idioma contrario (misma ruta)
    if (hostname.includes(DOMAIN_ES)) {
      window.location.href = window.location.href.replace(DOMAIN_ES, DOMAIN_EN);
      return;
    }
    if (hostname.includes(DOMAIN_EN)) {
      window.location.href = window.location.href.replace(DOMAIN_EN, DOMAIN_ES);
      return;
    }

    // En localhost / staging: toggle normal con persistencia en localStorage
    setLang((prev) => {
      const next: Language = prev === 'es' ? 'en' : 'es';
      try { localStorage.setItem('rx_lang', next); } catch { /* ignore */ }
      return next;
    });
  }, []);

  return (
    <LanguageContext.Provider value={{ lang, t: translations[lang], toggleLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage debe estar dentro de <LanguageProvider>');
  return ctx;
}
